import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ClipboardList,
  HelpCircle,
  History,
  Settings2,
  Shield,
  Sparkles,
  Trophy,
  Users2,
} from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { ROUTES } from "@/constants/routes";
import { Card } from "@/components/atoms/Card";

const MENU = [
  { key: "geral", icon: Settings2, label: "Configurações Gerais", description: "Nome, privacidade, limites e permissões", viewOnlyForNonAdmin: true },
  { key: "pontuacao", icon: Trophy, label: "Pontuação", description: "Regras de pontuação (congeladas na criação)", viewOnlyForNonAdmin: true },
  { key: "rodada", icon: Sparkles, label: "Config. da rodada", description: "Gol de Placa e Gol de Ouro por partida", viewOnlyForNonAdmin: false },
  { key: "clubes", icon: Shield, label: "Selecionar Clubes", description: "Ative ou desative clubes da competição", viewOnlyForNonAdmin: false },
  { key: "participantes", icon: Users2, label: "Participantes", description: "Papéis, pendentes e remoção", viewOnlyForNonAdmin: true },
  { key: "importar-pontos", icon: ClipboardList, label: "Importar Pontos", description: "Bônus e descontos manuais, com motivo", viewOnlyForNonAdmin: false },
  { key: "perguntas-bonus", icon: HelpCircle, label: "Perguntas Bônus", description: "Crie perguntas extras por rodada", viewOnlyForNonAdmin: false },
  { key: "historico", icon: History, label: "Histórico de Pontuação", description: "Auditoria completa de alterações", viewOnlyForNonAdmin: true },
] as const;

const ROUTE_MAP: Record<(typeof MENU)[number]["key"], (id: string) => string> = {
  geral: ROUTES.leagueSettingsGeneral,
  pontuacao: ROUTES.leagueSettingsScoring,
  clubes: ROUTES.leagueSettingsClubs,
  participantes: ROUTES.leagueSettingsParticipants,
  "importar-pontos": ROUTES.leagueSettingsImportPoints,
  "perguntas-bonus": ROUTES.leagueSettingsBonusQuestions,
  rodada: ROUTES.leagueSettingsRoundConfig,
  historico: ROUTES.leagueSettingsHistory,
};

export function LeagueSettingsPage() {
  const { league } = useLeagueContext();
  const navigate = useNavigate();
  const canManage = league.myRole === "admin" || league.myRole === "moderator";
  const visibleMenu = canManage ? MENU : MENU.filter((m) => m.viewOnlyForNonAdmin);

  return (
    <div className="space-y-2">
      <p className="px-1 text-xs text-muted-foreground">
        {canManage ? (
          <>
            Área administrativa da liga <strong className="text-foreground">{league.nome}</strong>.
          </>
        ) : (
          <>
            Modo visualização — só o administrador da liga <strong className="text-foreground">{league.nome}</strong> pode alterar
            essas configurações.
          </>
        )}
      </p>
      {visibleMenu.map(({ key, icon: Icon, label, description }) => (
        <Card key={key} className="border-0 p-0 shadow-soft">
          <button
            onClick={() => navigate(ROUTE_MAP[key](league.id))}
            className="flex w-full items-center gap-3 rounded-2xl p-4 text-left transition-colors hover:bg-muted/50"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-semibold">{label}</p>
              <p className="truncate text-xs text-muted-foreground">{description}</p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </Card>
      ))}
    </div>
  );
}
